import { useEffect, useState } from "react";
import { CheckCircle2, MessageCircle, QrCode, BookOpen, Images, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { BOOK_INCLUDES, WA_NUMBER_1, WA_NUMBER_2, WA_BOOK_MSG, waLink, trackEvent, openPaymentModal, PREVIEW_PAGES } from "@/data/content";

const BookSection = () => {
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    if (zoom === null) return;
    const onKey = (e) => e.key === "Escape" && setZoom(null);
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [zoom]);

  const flip = (dir) =>
    setZoom((z) => (z + dir + PREVIEW_PAGES.length) % PREVIEW_PAGES.length);

  return (
  <section id="book" data-testid="book-section" className="py-20 lg:py-28 bg-white scroll-mt-24">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        <div>
          <SectionHeading
            eyebrow="The IQMIZE Book"
            title="7-Day Student Memory & Learning System™"
            description="Student Workbook + Parent's Guide + Tips & Tools — a structured introduction to better learning, available as a digital PDF."
          />
          <div className="mt-8 grid sm:grid-cols-2 gap-3" data-testid="book-includes-grid">
            {BOOK_INCLUDES.map((item) => (
              <Reveal key={item} className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-brand-light px-4 py-3">
                <CheckCircle2 size={17} className="shrink-0 text-brand" />
                <span className="text-sm font-semibold text-slate-800">{item}</span>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.15}>
          <div data-testid="book-purchase-card" className="rounded-3xl border-2 border-brand/30 bg-gradient-to-b from-brand-light to-white p-8 lg:p-10 shadow-[0_20px_40px_rgb(3,105,161,0.10)]">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand text-white">
                <BookOpen size={24} strokeWidth={1.8} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand">Digital PDF</p>
                <p className="text-base font-bold text-slate-900">Instant access after payment</p>
              </div>
            </div>

            <div className="mt-7 flex items-end gap-4">
              <span className="text-5xl font-extrabold text-slate-900">₹49</span>
              <span className="pb-1.5 text-xl text-slate-400 line-through">₹780</span>
              <span className="pb-1.5 rounded-full bg-igold px-3 py-1 text-xs font-extrabold uppercase tracking-wide text-slate-900">
                94% off
              </span>
            </div>

            <div className="mt-7 space-y-3">
              <a
                href={waLink(WA_NUMBER_1, WA_BOOK_MSG)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="book-whatsapp-btn-1"
                onClick={() => trackEvent("whatsapp_click", { source: "book", number: WA_NUMBER_1 })}
                className="flex w-full items-center justify-center gap-2.5 rounded-full bg-brand px-8 py-4 text-base font-bold text-white shadow-md transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-brand-dark hover:shadow-xl"
              >
                <MessageCircle size={19} /> WhatsApp 70346 18014
              </a>
              <a
                href={waLink(WA_NUMBER_2, WA_BOOK_MSG)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="book-whatsapp-btn-2"
                onClick={() => trackEvent("whatsapp_click", { source: "book", number: WA_NUMBER_2 })}
                className="flex w-full items-center justify-center gap-2.5 rounded-full border-2 border-brand bg-white px-8 py-4 text-base font-bold text-brand transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-brand-light hover:shadow-lg"
              >
                <MessageCircle size={19} /> WhatsApp 85905 66268
              </a>
            </div>

            <div className="mt-7 border-t border-slate-200 pt-6">
              <button
                type="button"
                onClick={() => openPaymentModal("book")}
                data-testid="book-upi-pay-btn"
                className="flex w-full items-center justify-center gap-2.5 rounded-full border-2 border-dashed border-brand/50 bg-white px-6 py-3.5 text-sm font-bold text-brand transition-colors duration-200 hover:bg-brand-light"
              >
                <QrCode size={18} /> Pay ₹49 via UPI QR
              </button>
              <p className="mt-3 text-center text-xs leading-relaxed text-slate-500">
                Scan to pay ₹49 &amp; WhatsApp the screenshot — get your download link instantly.
              </p>
            </div>
          </div>
        </Reveal>
      </div>

      {/* Look Inside gallery */}
      <Reveal className="mt-16 lg:mt-20">
        <div data-testid="look-inside-gallery" id="look-inside" className="scroll-mt-24">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand text-white">
              <Images size={20} strokeWidth={1.8} />
            </div>
            <div>
              <h3 className="text-xl lg:text-2xl font-bold tracking-tight text-slate-900">Look Inside</h3>
              <p className="text-sm text-slate-500">Tap any page to read it full-screen — see exactly what you're getting.</p>
            </div>
          </div>

          <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-5">
            {PREVIEW_PAGES.map((p, i) => (
              <button
                key={p.label}
                type="button"
                data-testid={`look-inside-thumb-${i + 1}`}
                onClick={() => {
                  setZoom(i);
                  trackEvent("look_inside_open", { source: "book-section", number: String(i + 1) });
                }}
                className="group text-left rounded-2xl border border-slate-200 bg-white p-2.5 shadow-sm transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_40px_rgb(0,0,0,0.10)] hover:border-brand/40"
              >
                <div className="aspect-[1055/1491] w-full overflow-hidden rounded-xl bg-brand-light">
                  <img
                    src={p.src}
                    alt={`Book page preview: ${p.label}`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <p className="mt-2.5 px-1 pb-1 text-xs font-bold text-slate-700 group-hover:text-brand transition-colors duration-200">
                  {p.label}
                </p>
              </button>
            ))}
          </div>
        </div>
      </Reveal>
    </div>

    {/* Full-screen page lightbox */}
    {zoom !== null && (
      <div
        data-testid="look-inside-lightbox"
        className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/90 p-4"
        onClick={() => setZoom(null)}
      >
        <img
          src={PREVIEW_PAGES[zoom].src}
          alt={`Book page full-screen: ${PREVIEW_PAGES[zoom].label}`}
          data-testid="look-inside-lightbox-image"
          className="max-h-full max-w-full object-contain rounded-lg shadow-2xl"
        />
        <p className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-white/95 px-5 py-2 text-xs font-bold text-slate-700 shadow-lg" data-testid="look-inside-lightbox-label">
          {PREVIEW_PAGES[zoom].label} · {zoom + 1} of {PREVIEW_PAGES.length}
        </p>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setZoom(null); }}
          data-testid="look-inside-close"
          aria-label="Close preview"
          className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-slate-700 shadow-lg transition-transform duration-200 hover:scale-110"
        >
          <X size={20} />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); flip(-1); }}
          data-testid="look-inside-prev"
          aria-label="Previous page"
          className="absolute left-3 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-white/95 text-inavy shadow-lg transition-transform duration-200 hover:scale-110"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); flip(1); }}
          data-testid="look-inside-next"
          aria-label="Next page"
          className="absolute right-3 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-white/95 text-inavy shadow-lg transition-transform duration-200 hover:scale-110"
        >
          <ChevronRight size={20} />
        </button>
      </div>
    )}
  </section>
  );
};

export default BookSection;
